import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Award, FileCheck, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { convalidationSteps, faqs, careers } from '../mockData';

export const Convalidation = () => {
  const convalidacionCareers = careers.filter(c => c.convalidationAvailable);

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 via-white to-blue-50 py-20 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-block px-4 py-2 bg-mit-red/10 rounded-full mb-6">
                <span className="text-mit-red font-semibold text-sm">Reconocimiento de Experiencia</span>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Convalidación de <span className="text-mit-red">Experiencia Laboral</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                ¿Tienes experiencia laboral pero no un título oficial? Valida tu trayectoria profesional y obtén tu título técnico a nombre de la nación en menos tiempo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto">
                  <Button size="lg" className="bg-mit-red hover:bg-red-700 text-white w-full sm:w-auto">
                    Evaluar mi Experiencia Gratis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a
                  href="https://wa.me/51944748443?text=Hola, visité la web y me gustaría mas información."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" variant="outline" className="border-2 border-gray-300 w-full sm:w-auto">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Consultar por WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative animate-fade-in-up animate-delay-200">
              <img
                src="https://customer-assets.emergentagent.com/job_mit-formacion/artifacts/7pprld1s_WhatsApp%20Image%202026-01-30%20at%204.06.31%20PM.jpeg"
                alt="Profesionales graduados"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué es la Convalidación? */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">¿Qué es la Convalidación?</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              La convalidación es un proceso mediante el cual <strong>validamos tu experiencia laboral comprobable</strong> como parte de tu formación técnica profesional. Esto te permite obtener tu <strong>título técnico a nombre de la nación</strong> en un tiempo significativamente menor al de una carrera regular.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover-lift">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-mit-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-mit-blue" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Menor Tiempo</h3>
                <p className="text-gray-600">
                  Reduce hasta 50% el tiempo necesario para obtener tu título profesional
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-mit-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-mit-red" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Reconocimiento Oficial</h3>
                <p className="text-gray-600">
                  Título técnico profesional con validez nacional
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mejores Oportunidades</h3>
                <p className="text-gray-600">
                  Accede a mejores posiciones laborales y aumenta tus ingresos
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Proceso en 3 Pasos */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-red-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Proceso en 3 Pasos</h2>
            <p className="text-xl text-gray-600">Sencillo, rápido y transparente</p>
          </div>

          <div className="max-w-4xl mx-auto">
            {convalidationSteps.map((step, index) => (
              <div key={step.step} className={`flex gap-6 mb-12 animate-fade-in-up animate-delay-${index * 100}`}>
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-mit-red rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {step.step}
                  </div>
                </div>
                <Card className="flex-1 hover-lift">
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ¿A quién va dirigido? */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">¿A quién va dirigido?</h2>
            
            <div className="space-y-6">
              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Trabajadores con Experiencia</h3>
                      <p className="text-gray-700">
                        Personas con mínimo 3 años de experiencia laboral comprobable en el área de la carrera técnica deseada.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Profesionales sin Título Formal</h3>
                      <p className="text-gray-700">
                        Personas que desarrollan funciones técnicas profesionales pero no cuentan con un título oficial.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-mit-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Personas en Crecimiento Profesional</h3>
                      <p className="text-gray-700">
                        Trabajadores que buscan ascender en sus organizaciones o mejorar su situación laboral mediante una certificación oficial.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Carreras Convalidbles */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Carreras Disponibles para Convalidación</h2>
            <p className="text-xl text-gray-600">Valida tu experiencia en estas especialidades</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {convalidacionCareers.map((career, index) => (
              <Card key={career.id} className={`hover-lift animate-fade-in-up animate-delay-${(index % 3) * 100}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-mit-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileCheck className="w-6 h-6 text-mit-blue" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{career.name}</h3>
                      <Link to={`/carreras/${career.id}`} className="text-mit-blue hover:text-blue-800 text-sm font-semibold">
                        Ver detalles →
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Preguntas Frecuentes */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Preguntas Frecuentes</h2>
            
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 bg-mit-red text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">¿Listo para Validar tu Experiencia?</h2>
          <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">
            Comienza hoy tu proceso de convalidación. Nuestros asesores te guiarán en cada paso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/51944748443?text=Hola, visité la web y me gustaría mas información."
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-white text-mit-red hover:bg-gray-100 w-full sm:w-auto">
                Solicitar Evaluación Gratuita
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a
              href="https://wa.me/51944748443?text=Hola, visité la web y me gustaría mas información."
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-mit-red w-full sm:w-auto">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Contáctanos por WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};